import { useCallback } from 'react';
import {
  useCreateSectionMutation,
  useUpdateSectionMutation,
  useDeleteSectionMutation
} from 'legacy/events/hooks';
import * as toast from 'legacy/common/utils/toast';
import { TOAST_MESSAGES } from '../constants';

interface UseSectionMutationsProps {
  eventId: string;
  onDeleteSection?: (sectionId: string) => void;
  onSectionUpdated?: () => void;
}

export const useSectionMutations = ({
  eventId,
  onDeleteSection,
  onSectionUpdated
}: UseSectionMutationsProps) => {
  const createSectionMutation = useCreateSectionMutation();
  const updateSectionMutation = useUpdateSectionMutation();
  const deleteSectionMutation = useDeleteSectionMutation();

  const updateSectionName = useCallback(
    (sectionId: string, name: string): Promise<void> =>
      new Promise((resolve) => {
        updateSectionMutation.mutate(
          { eventId, sectionId, name },
          {
            onSuccess: () => {
              onSectionUpdated?.();
              resolve();
            },
            onError: (error) => {
              toast.showError({
                title: TOAST_MESSAGES.SECTION_UPDATE_ERROR,
                message: error.message
              });
              resolve();
            }
          }
        );
      }),
    [eventId, updateSectionMutation, onSectionUpdated]
  );

  const deleteSection = useCallback(
    (sectionId: string) => {
      deleteSectionMutation.mutate(
        { eventId, sectionId },
        {
          onSuccess: () => {
            onDeleteSection?.(sectionId);
          },
          onError: (error) => {
            toast.showError({
              title: TOAST_MESSAGES.SECTION_DELETE_ERROR,
              message: error.message
            });
          }
        }
      );
    },
    [eventId, deleteSectionMutation, onDeleteSection]
  );

  return {
    createSectionMutation,
    updateSectionMutation,
    deleteSectionMutation,
    updateSectionName,
    deleteSection
  };
}; 